import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import CopyPricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/CopyPricingPlanVersionFormModal';
import DeletePricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/DeletePricingPlanVersionFormModal';
import UpdatePricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/UpdatePricingPlanVersionFormModal';
import { PRICING_PLAN_STATUS, PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import NavLink from 'src/registry/components/navigation/NavLink';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

export default function CellPricingPlanVersionActions({ row }: CellContext<PricingPlan, unknown>) {
    const { formatMessage } = useIntl();
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    if (
        new Date(row.original.valid_from).getTime() <= new Date().getTime() ||
        row.original.status === PRICING_PLAN_STATUS.PUBLISHED
    )
        return null;

    return (
        <div className="flex gap-1">
            <TooltipProvider>
                {managePricingPlans && (
                    <>
                        <Tooltip>
                            <CopyPricingPlanVersionFormModal
                                selectedPricingPlan={row.original}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlanVersion).toLowerCase(),
                                }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.COPY} />
                                    </Button>
                                </TooltipTrigger>
                            </CopyPricingPlanVersionFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.copy} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <UpdatePricingPlanVersionFormModal
                                selectedPricingPlan={row.original}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlanVersion).toLowerCase(),
                                }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdatePricingPlanVersionFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeletePricingPlanVersionFormModal
                                selectedPricingPlan={row.original}
                                semantics={{
                                    entryName: formatMessage(pricingPlanMessages.pricingPlanVersion).toLowerCase(),
                                }}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeletePricingPlanVersionFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <NavLink
                            routerClassName={() => ''}
                            to={`/${PATH.ADMINISTRATION}/pricing-plan/${row.original.id}`}
                        >
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </NavLink>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
